import Reveal, { SectionLabel } from "@/components/Reveal";
import { CalendarDays, MonitorPlay, Zap, MessagesSquare, Clock, ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const MARKETS = [
  { name: "XAUUSD · Gold", note: "Core focus", core: true },
  { name: "NASDAQ", note: "Indices" },
  { name: "Silver", note: "Metals" },
  { name: "S&P 500", note: "Indices" },
  { name: "US30", note: "Indices" },
  { name: "Major FX Pairs", note: "Forex" },
];

const SESSIONS = [
  {
    name: "London Session",
    desc: "The daily blueprint lands before the open — bias, key zones and the scenarios we're watching.",
  },
  {
    name: "New York Session",
    desc: "As the US comes in: what's changed, what's invalidated and what's still live — updated in the room.",
  },
];

const FEATURES = [
  { icon: CalendarDays, title: "Daily Market Blueprints", desc: "Key zones, bias and scenarios mapped before the session opens — not after the move." },
  { icon: MonitorPlay, title: "Live Chart Demonstrations", desc: "Watch the Atlas framework applied in real time: markups, entry logic and management, narrated live." },
  { icon: Zap, title: "Early Insights", desc: "Developing ideas shared with members first — while the setup is still forming, not after it's done." },
  { icon: MessagesSquare, title: "Priority Q&A", desc: "Direct access to the Atlas team. Your chart questions answered by a human, not left on read." },
];

const Exclusive = () => (
  <section id="exclusive" className="relative py-24 lg:py-36 bg-panel/30 border-y border-line/60" data-testid="exclusive-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-3xl mb-14 lg:mb-20">
        <Reveal>
          <SectionLabel>Atlas Exclusive</SectionLabel>
          <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-50 leading-tight" data-testid="exclusive-title">
            Inside the rooms
            <span className="text-gold-gradient"> most traders never see.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed">
            The members-only side of Atlas: multi-market analysis, full session coverage and live
            chart demonstrations — all inside the private Telegram rooms, all included with the
            Lifetime Access Pass.
          </p>
        </Reveal>
      </div>

      <Reveal>
        <div className="border border-line rounded-md bg-card2/40 p-6 sm:p-8" data-testid="exclusive-markets">
          <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase">Multi-Market Coverage</p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {MARKETS.map((m, i) => (
              <div
                key={m.name}
                data-testid={`exclusive-market-${i + 1}`}
                className={`rounded-sm border px-4 py-3.5 text-center transition-colors duration-500 ${
                  m.core
                    ? "border-gold/50 bg-gold/10 gold-ring"
                    : "border-line bg-ink/50 hover:border-gold/30"
                }`}
              >
                <p className={`font-display font-bold text-sm ${m.core ? "text-gold" : "text-slate-100"}`}>{m.name}</p>
                <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-slate-500">{m.note}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-slate-500">
            Gold is home turf — the same Atlas framework is applied across indices, metals and major FX pairs.
          </p>
        </div>
      </Reveal>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        {SESSIONS.map((s, i) => (
          <Reveal key={s.name} delay={0.1 + i * 0.1}>
            <div className="h-full border border-line rounded-md bg-card2/40 hover:border-gold/40 transition-colors duration-500 p-6 sm:p-8" data-testid={`exclusive-session-${i + 1}`}>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                  <Clock size={17} className="text-gold" />
                </span>
                <h3 className="font-display font-bold text-lg text-slate-100">{s.name}</h3>
              </div>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.08}>
            <div className="h-full border border-line rounded-md bg-card2/40 hover:border-gold/40 transition-colors duration-500 p-6" data-testid={`exclusive-feature-${i + 1}`}>
              <f.icon size={20} className="text-gold" />
              <h3 className="mt-4 font-display font-bold text-base text-slate-100 leading-snug">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border border-gold/30 rounded-md bg-gold/5 px-6 py-6 sm:px-8" data-testid="exclusive-cta-strip">
          <p className="text-sm text-slate-300 leading-relaxed max-w-xl">
            <span className="text-gold font-semibold">All of it</span> — the rooms, the blueprints, the live
            demos and the multi-market coverage — is included in the £450 Lifetime Access Pass.
          </p>
          <button
            onClick={() => scrollToSection("join")}
            data-testid="exclusive-cta-button"
            className="group inline-flex shrink-0 items-center gap-2.5 bg-gold text-ink font-semibold text-sm px-6 py-3 rounded-sm hover:bg-gold-bright transition-colors duration-300"
          >
            Unlock Atlas Exclusive
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

export default Exclusive;
